import { useMutation, useQuery } from "@apollo/client/react";
import {
  DEFAULT_PAGE_REQUEST,
  type IEarningRule,
  type IPage,
  type IRewardAuditEntry,
  type IRewardMember,
  type IRewardMemberDetail,
  type IRewardsDashboard,
  type IEarningRuleInput,
  type IEntityReference,
  type IPageRequest,
  type IPointAdjustmentInput,
  type IRedemption,
  type IRewardCatalogItem,
  type IRewardCatalogItemInput,
  type IRewardProgram,
  type IRewardProgramInput,
  type IRewardTier,
  type IRewardTierInput,
  type IRewardTransaction,
  type IRuleSimulation,
  type IRuleSimulationContext,
} from "@ticketon/rewards-core";
import {
  ADJUST_REWARD_MEMBER_POINTS,
  CANCEL_REWARD_REDEMPTION_ADMIN,
  CHANGE_REWARD_CATALOG_ITEM_STATUS,
  CHANGE_REWARD_PROGRAM_STATUS,
  CHANGE_REWARD_RULE_STATUS,
  CREATE_REWARD_CATALOG_ITEM,
  CREATE_REWARD_PROGRAM,
  CREATE_REWARD_RULE,
  CREATE_REWARD_TIER,
  DELETE_REWARD_TIER,
  DUPLICATE_REWARD_PROGRAM,
  DUPLICATE_REWARD_RULE,
  FULFILL_REWARD_REDEMPTION,
  GET_REWARD_AUDIT_LOG,
  GET_REWARD_CATALOG_ITEM,
  GET_REWARD_CATALOG_ITEMS,
  GET_REWARD_MEMBER,
  GET_REWARD_MEMBER_REDEMPTIONS,
  GET_REWARD_MEMBER_TRANSACTIONS,
  GET_REWARD_PROGRAM,
  GET_REWARD_PROGRAMS,
  GET_REWARD_REDEMPTION,
  GET_REWARD_REDEMPTIONS,
  GET_REWARD_RULE,
  GET_REWARD_RULES,
  GET_REWARD_TIER,
  GET_REWARD_TIERS,
  GET_REWARDS_DASHBOARD,
  REORDER_REWARD_TIERS,
  REVERSE_REWARD_REDEMPTION,
  SEARCH_REWARD_MEMBERS,
  REWARD_ENTITY_SEARCHES,
  SIMULATE_REWARD_RULE,
  UPDATE_REWARD_CATALOG_ITEM,
  UPDATE_REWARD_PROGRAM,
  UPDATE_REWARD_RULE,
  UPDATE_REWARD_TIER,
  type IAuditFilterInput,
  type ICatalogAdminFilterInput,
  type IMemberSearchInput,
  type IProgramFilterInput,
  type IRedemptionAdminFilterInput,
  type IRuleFilterInput,
  type RewardEntityKind,
} from "../documents/admin";
import { readPage, toPageVariables } from "../pagination";
import { useRewardsApolloClient } from "./client";
import {
  useMutationState,
  useQueryState,
  type IMutationState,
  type IQueryState,
  type IQueryStateWithDefault,
} from "./shared";

/*
 * Admin hooks run against the `/platform/admin/rewards/graphql` endpoint. Wrap
 * the admin screens in `<RewardsApolloProvider client={rewardsAdminClient}>`.
 */

const NO_TIERS: IRewardTier[] = [];
const NO_ENTITIES: IEntityReference[] = [];

/* ----------------------------- Dashboard ------------------------------ */

export function useRewardsDashboard(
  range: { from?: string; to?: string } = {},
): IQueryState<IRewardsDashboard> {
  const client = useRewardsApolloClient();
  const result = useQuery(GET_REWARDS_DASHBOARD, { client, variables: range });
  return useQueryState(result, (data) => data.rewardsDashboard);
}

/* ------------------------------ Programs ------------------------------ */

export function useRewardPrograms(
  params: { page?: IPageRequest; filter?: IProgramFilterInput } = {},
): IQueryState<IPage<IRewardProgram>> {
  const client = useRewardsApolloClient();
  const result = useQuery(GET_REWARD_PROGRAMS, {
    client,
    variables: { ...toPageVariables(params.page ?? DEFAULT_PAGE_REQUEST), filter: params.filter },
  });
  return useQueryState(result, (data) => readPage(data.rewardPrograms));
}

export function useRewardProgram(id: string | undefined): IQueryState<IRewardProgram | null> {
  const client = useRewardsApolloClient();
  const result = useQuery(GET_REWARD_PROGRAM, { client, variables: { id: id ?? "" }, skip: !id });
  return useQueryState(result, (data) => data.rewardProgram ?? null);
}

export function useCreateRewardProgram(): IMutationState<
  { createRewardProgram: IRewardProgram },
  { input: IRewardProgramInput }
> {
  const client = useRewardsApolloClient();
  return useMutationState(
    useMutation(CREATE_REWARD_PROGRAM, {
      client,
      refetchQueries: [GET_REWARD_PROGRAMS],
    }),
  );
}

export function useUpdateRewardProgram(): IMutationState<
  { updateRewardProgram: IRewardProgram },
  { id: string; input: IRewardProgramInput }
> {
  const client = useRewardsApolloClient();
  return useMutationState(
    useMutation(UPDATE_REWARD_PROGRAM, {
      client,
      refetchQueries: [GET_REWARD_PROGRAMS],
    }),
  );
}

export function useChangeRewardProgramStatus(): IMutationState<
  { changeRewardProgramStatus: IRewardProgram },
  { id: string; status: string }
> {
  const client = useRewardsApolloClient();
  return useMutationState(
    useMutation(CHANGE_REWARD_PROGRAM_STATUS, {
      client,
      refetchQueries: [GET_REWARD_PROGRAMS, GET_REWARDS_DASHBOARD],
    }),
  );
}

export function useDuplicateRewardProgram(): IMutationState<
  { duplicateRewardProgram: IRewardProgram },
  { id: string }
> {
  const client = useRewardsApolloClient();
  return useMutationState(
    useMutation(DUPLICATE_REWARD_PROGRAM, {
      client,
      refetchQueries: [GET_REWARD_PROGRAMS],
    }),
  );
}

/* -------------------------------- Tiers ------------------------------- */

export function useRewardTiers(programId: string | undefined): IQueryStateWithDefault<IRewardTier[]> {
  const client = useRewardsApolloClient();
  const result = useQuery(GET_REWARD_TIERS, {
    client,
    variables: { programId: programId ?? "" },
    skip: !programId,
  });
  return useQueryState(result, (data) => data.rewardTiers, NO_TIERS);
}

export function useRewardTier(id: string | undefined): IQueryState<IRewardTier | null> {
  const client = useRewardsApolloClient();
  const result = useQuery(GET_REWARD_TIER, { client, variables: { id: id ?? "" }, skip: !id });
  return useQueryState(result, (data) => data.rewardTier ?? null);
}

export function useCreateRewardTier(): IMutationState<
  { createRewardTier: IRewardTier },
  { input: IRewardTierInput }
> {
  const client = useRewardsApolloClient();
  return useMutationState(
    useMutation(CREATE_REWARD_TIER, {
      client,
      refetchQueries: [GET_REWARD_TIERS],
    }),
  );
}

export function useUpdateRewardTier(): IMutationState<
  { updateRewardTier: IRewardTier },
  { id: string; input: IRewardTierInput }
> {
  const client = useRewardsApolloClient();
  return useMutationState(
    useMutation(UPDATE_REWARD_TIER, {
      client,
      refetchQueries: [GET_REWARD_TIERS],
    }),
  );
}

export function useDeleteRewardTier(): IMutationState<
  { deleteRewardTier: { id: string } },
  { id: string }
> {
  const client = useRewardsApolloClient();
  return useMutationState(
    useMutation(DELETE_REWARD_TIER, {
      client,
      refetchQueries: [GET_REWARD_TIERS],
    }),
  );
}

export function useReorderRewardTiers(): IMutationState<
  { reorderRewardTiers: IRewardTier[] },
  { programId: string; orderedTierIds: string[] }
> {
  const client = useRewardsApolloClient();
  return useMutationState(
    useMutation(REORDER_REWARD_TIERS, {
      client,
      refetchQueries: [GET_REWARD_TIERS],
    }),
  );
}

/* -------------------------------- Rules ------------------------------- */

export function useRewardRules(
  params: { page?: IPageRequest; filter?: IRuleFilterInput } = {},
): IQueryState<IPage<IEarningRule>> {
  const client = useRewardsApolloClient();
  const result = useQuery(GET_REWARD_RULES, {
    client,
    variables: { ...toPageVariables(params.page ?? DEFAULT_PAGE_REQUEST), filter: params.filter },
  });
  return useQueryState(result, (data) => readPage(data.rewardRules));
}

export function useRewardRule(id: string | undefined): IQueryState<IEarningRule | null> {
  const client = useRewardsApolloClient();
  const result = useQuery(GET_REWARD_RULE, { client, variables: { id: id ?? "" }, skip: !id });
  return useQueryState(result, (data) => data.rewardRule ?? null);
}

export function useCreateRewardRule(): IMutationState<
  { createRewardRule: IEarningRule },
  { input: IEarningRuleInput }
> {
  const client = useRewardsApolloClient();
  return useMutationState(
    useMutation(CREATE_REWARD_RULE, {
      client,
      refetchQueries: [GET_REWARD_RULES],
    }),
  );
}

export function useUpdateRewardRule(): IMutationState<
  { updateRewardRule: IEarningRule },
  { id: string; input: IEarningRuleInput }
> {
  const client = useRewardsApolloClient();
  return useMutationState(
    useMutation(UPDATE_REWARD_RULE, {
      client,
      refetchQueries: [GET_REWARD_RULES],
    }),
  );
}

export function useChangeRewardRuleStatus(): IMutationState<
  { changeRewardRuleStatus: IEarningRule },
  { id: string; status: string }
> {
  const client = useRewardsApolloClient();
  return useMutationState(
    useMutation(CHANGE_REWARD_RULE_STATUS, {
      client,
      refetchQueries: [GET_REWARD_RULES, GET_REWARDS_DASHBOARD],
    }),
  );
}

export function useDuplicateRewardRule(): IMutationState<
  { duplicateRewardRule: IEarningRule },
  { id: string }
> {
  const client = useRewardsApolloClient();
  return useMutationState(
    useMutation(DUPLICATE_REWARD_RULE, {
      client,
      refetchQueries: [GET_REWARD_RULES],
    }),
  );
}

export function useSimulateRewardRule(): IMutationState<
  { simulateRewardRule: IRuleSimulation },
  { programId: string; context: IRuleSimulationContext }
> {
  const client = useRewardsApolloClient();
  return useMutationState(
    useMutation(SIMULATE_REWARD_RULE, {
      client,
    }),
  );
}

/* ------------------------------ Catalog ------------------------------- */

export function useRewardCatalogItems(
  params: { page?: IPageRequest; filter?: ICatalogAdminFilterInput } = {},
): IQueryState<IPage<IRewardCatalogItem>> {
  const client = useRewardsApolloClient();
  const result = useQuery(GET_REWARD_CATALOG_ITEMS, {
    client,
    variables: { ...toPageVariables(params.page ?? DEFAULT_PAGE_REQUEST), filter: params.filter },
  });
  return useQueryState(result, (data) => readPage(data.rewardCatalogItems));
}

export function useRewardCatalogItem(id: string | undefined): IQueryState<IRewardCatalogItem | null> {
  const client = useRewardsApolloClient();
  const result = useQuery(GET_REWARD_CATALOG_ITEM, { client, variables: { id: id ?? "" }, skip: !id });
  return useQueryState(result, (data) => data.rewardCatalogItem ?? null);
}

export function useCreateRewardCatalogItem(): IMutationState<
  { createRewardCatalogItem: IRewardCatalogItem },
  { input: IRewardCatalogItemInput }
> {
  const client = useRewardsApolloClient();
  return useMutationState(
    useMutation(CREATE_REWARD_CATALOG_ITEM, {
      client,
      refetchQueries: [GET_REWARD_CATALOG_ITEMS],
    }),
  );
}

export function useUpdateRewardCatalogItem(): IMutationState<
  { updateRewardCatalogItem: IRewardCatalogItem },
  { id: string; input: IRewardCatalogItemInput }
> {
  const client = useRewardsApolloClient();
  return useMutationState(
    useMutation(UPDATE_REWARD_CATALOG_ITEM, {
      client,
      refetchQueries: [GET_REWARD_CATALOG_ITEMS],
    }),
  );
}

export function useChangeRewardCatalogItemStatus(): IMutationState<
  { changeRewardCatalogItemStatus: IRewardCatalogItem },
  { id: string; status: string }
> {
  const client = useRewardsApolloClient();
  return useMutationState(
    useMutation(CHANGE_REWARD_CATALOG_ITEM_STATUS, {
      client,
      refetchQueries: [GET_REWARD_CATALOG_ITEMS],
    }),
  );
}

/* ------------------------------ Members ------------------------------- */

export function useSearchRewardMembers(params: {
  input: IMemberSearchInput;
  page?: IPageRequest;
  skip?: boolean;
}): IQueryState<IPage<IRewardMember>> {
  const client = useRewardsApolloClient();
  const result = useQuery(SEARCH_REWARD_MEMBERS, {
    client,
    variables: { ...toPageVariables(params.page ?? DEFAULT_PAGE_REQUEST), input: params.input },
    skip: params.skip ?? !params.input.query.trim(),
  });
  return useQueryState(result, (data) => readPage(data.searchRewardMembers));
}

export function useRewardMember(id: string | undefined): IQueryState<IRewardMemberDetail | null> {
  const client = useRewardsApolloClient();
  const result = useQuery(GET_REWARD_MEMBER, { client, variables: { id: id ?? "" }, skip: !id });
  return useQueryState(result, (data) => data.rewardMember ?? null);
}

export function useRewardMemberTransactions(
  memberId: string | undefined,
  page: IPageRequest = DEFAULT_PAGE_REQUEST,
): IQueryState<IPage<IRewardTransaction>> {
  const client = useRewardsApolloClient();
  const result = useQuery(GET_REWARD_MEMBER_TRANSACTIONS, {
    client,
    variables: { memberId: memberId ?? "", ...toPageVariables(page) },
    skip: !memberId,
  });
  return useQueryState(result, (data) => readPage(data.rewardMemberTransactions));
}

export function useRewardMemberRedemptions(
  memberId: string | undefined,
  page: IPageRequest = DEFAULT_PAGE_REQUEST,
): IQueryState<IPage<IRedemption>> {
  const client = useRewardsApolloClient();
  const result = useQuery(GET_REWARD_MEMBER_REDEMPTIONS, {
    client,
    variables: { memberId: memberId ?? "", ...toPageVariables(page) },
    skip: !memberId,
  });
  return useQueryState(result, (data) => readPage(data.rewardMemberRedemptions));
}

export function useAdjustRewardMemberPoints(): IMutationState<
  { adjustRewardMemberPoints: IRewardTransaction },
  { input: IPointAdjustmentInput }
> {
  const client = useRewardsApolloClient();
  return useMutationState(
    useMutation(ADJUST_REWARD_MEMBER_POINTS, {
      client,
      refetchQueries: [GET_REWARD_MEMBER, GET_REWARD_MEMBER_TRANSACTIONS, GET_REWARD_AUDIT_LOG],
      awaitRefetchQueries: true,
    }),
  );
}

/* ---------------------------- Redemptions ----------------------------- */

export function useRewardRedemptions(
  params: { page?: IPageRequest; filter?: IRedemptionAdminFilterInput } = {},
): IQueryState<IPage<IRedemption>> {
  const client = useRewardsApolloClient();
  const result = useQuery(GET_REWARD_REDEMPTIONS, {
    client,
    variables: { ...toPageVariables(params.page ?? DEFAULT_PAGE_REQUEST), filter: params.filter },
  });
  return useQueryState(result, (data) => readPage(data.rewardRedemptions));
}

export function useRewardRedemption(id: string | undefined): IQueryState<IRedemption | null> {
  const client = useRewardsApolloClient();
  const result = useQuery(GET_REWARD_REDEMPTION, { client, variables: { id: id ?? "" }, skip: !id });
  return useQueryState(result, (data) => data.rewardRedemption ?? null);
}

export function useFulfillRewardRedemption(): IMutationState<
  { fulfillRewardRedemption: IRedemption },
  { id: string; note?: string }
> {
  const client = useRewardsApolloClient();
  return useMutationState(
    useMutation(FULFILL_REWARD_REDEMPTION, {
      client,
      refetchQueries: [GET_REWARD_REDEMPTIONS, GET_REWARD_MEMBER_REDEMPTIONS],
    }),
  );
}

export function useCancelRewardRedemptionAdmin(): IMutationState<
  { cancelRewardRedemption: IRedemption },
  { id: string; reason: string }
> {
  const client = useRewardsApolloClient();
  return useMutationState(
    useMutation(CANCEL_REWARD_REDEMPTION_ADMIN, {
      client,
      refetchQueries: [GET_REWARD_REDEMPTIONS, GET_REWARD_MEMBER_REDEMPTIONS, GET_REWARD_MEMBER, GET_REWARD_MEMBER_TRANSACTIONS],
      awaitRefetchQueries: true,
    }),
  );
}

export function useReverseRewardRedemption(): IMutationState<
  { reverseRewardRedemption: IRedemption },
  { id: string; reason: string }
> {
  const client = useRewardsApolloClient();
  return useMutationState(
    useMutation(REVERSE_REWARD_REDEMPTION, {
      client,
      refetchQueries: [GET_REWARD_REDEMPTIONS, GET_REWARD_MEMBER_REDEMPTIONS, GET_REWARD_MEMBER, GET_REWARD_MEMBER_TRANSACTIONS],
      awaitRefetchQueries: true,
    }),
  );
}

/* ------------------------------- Audit -------------------------------- */

export function useRewardAuditLog(
  params: { page?: IPageRequest; filter?: IAuditFilterInput } = {},
): IQueryState<IPage<IRewardAuditEntry>> {
  const client = useRewardsApolloClient();
  const result = useQuery(GET_REWARD_AUDIT_LOG, {
    client,
    variables: { ...toPageVariables(params.page ?? DEFAULT_PAGE_REQUEST), filter: params.filter },
  });
  return useQueryState(result, (data) => readPage(data.rewardAuditLog));
}

/* -------------------------- Entity search ----------------------------- */

/**
 * Unified entity-search hook for the rule builder. Skips the request until a
 * non-empty query is provided; debouncing is the caller's responsibility.
 * `data` is `[]` while skipped or loading.
 */
export function useSearchRewardEntities(
  kind: RewardEntityKind,
  query: string,
  limit = 10,
): IQueryStateWithDefault<IEntityReference[]> {
  const client = useRewardsApolloClient();
  const search = REWARD_ENTITY_SEARCHES[kind];
  const result = useQuery(search.document, {
    client,
    variables: { query, limit },
    skip: query.trim().length === 0,
  });
  return useQueryState(result, (data) => data[search.field] ?? NO_ENTITIES, NO_ENTITIES);
}
